import {
    IFundingDestinations,
    IIngoFundingCancelledEvent,
    IIngoIFrameParams,
    IIngoMaxVerificationAttemptsExceededEvent,
    IIngoPageLoadEvent,
    IIngoPluginErrorEvent,
    IIngoSessionErrorEvent,
    IIngoTerminalFailureAcknowledgedEvent,
    IIngoTokenSuccessEvent,
    IIngoUnsupportedBrowserDetectedEvent
} from './ingo-types';
import { IQueryParamKeys } from './types';

interface IRequestPaymentMethodControls {
    successDialog: HTMLDialogElement;
}

class IngoIFrame {
    private static readonly IFrameDomElementId: string =
        'ingo-instant-payments-iframe';
    private static readonly IFrameErrorDialogId: string = 'iframeErrorDialog';
    private static readonly PaymentMethodRegisteredDialogId: string =
        'paymentMethodRegisteredDialog';
    private static readonly PaymentMethodNotRegisteredDialogId: string =
        'paymentMethodNotRegisteredDialog';
    private static readonly LoadingIndicatorId: string =
        'ingo-loading-indicator';

    /**
     * Loading timeout duration in milliseconds.
     * If the iframe doesn't fire a PAGE_LOAD event within this time, show an error dialog.
     */
    private static readonly LoadingTimeoutDuration: number = 30 * 1000; // 30 seconds

    /**
     * @deprecated Use IFrameDomElementId instead. This alias is for backward compatibility.
     */
    public static get IFrameDOMElementID(): string {
        return IngoIFrame.IFrameDomElementId;
    }

    private ingoPlugin: any;
    private readonly fundingDestinations!: IFundingDestinations;
    private readonly ingoIFrameParams!: IIngoIFrameParams;
    private readonly queryParamKeys!: IQueryParamKeys;

    private readonly fundingDestination!: string;

    /**
     * Timeout ID for the loading timeout.
     * This fires if the iframe doesn't load (no PAGE_LOAD event) within the timeout duration.
     * Unlike an activity timeout, this only fires once and clears permanently when iframe loads.
     */
    private loadingTimeoutId?: number;

    /**
     * Instance-specific loading timeout duration (can be overridden for testing).
     */
    private readonly loadingTimeoutDuration: number;

    /**
     * Flag indicating whether the iframe has successfully loaded (received PAGE_LOAD event).
     * Once true, the loading timeout will never fire.
     */
    private hasLoaded: boolean = false;

    get controls(): IRequestPaymentMethodControls {
        return {
            successDialog: document.getElementById(
                IngoIFrame.PaymentMethodRegisteredDialogId
            ) as HTMLDialogElement
        } satisfies IRequestPaymentMethodControls;
    }

    get ingoInstantPayments() {
        return (window as any)['IngoInstantPayments'];
    }

    get iframeContainerElement(): HTMLElement | null {
        return document.getElementById(IngoIFrame.IFrameDomElementId);
    }

    constructor() {
        // Allow loading timeout to be overridden for testing via localStorage
        // Usage: localStorage.setItem('ingo-loading-timeout', '3000')
        const timeoutOverride = localStorage.getItem('ingo-loading-timeout');
        this.loadingTimeoutDuration = timeoutOverride
            ? parseInt(timeoutOverride, 10)
            : IngoIFrame.LoadingTimeoutDuration;

        const fundingDestinationElement: HTMLScriptElement | null =
            document.getElementById('fundingDestinations') as HTMLScriptElement;
        if (Boolean(fundingDestinationElement?.textContent)) {
            this.fundingDestinations = JSON.parse(
                fundingDestinationElement?.textContent ?? '{}'
            ) as IFundingDestinations;
        }

        const ingoIFrameParamsElement: HTMLScriptElement | null =
            document.getElementById('ingoIFrameParams') as HTMLScriptElement;
        if (Boolean(ingoIFrameParamsElement?.textContent)) {
            this.ingoIFrameParams = JSON.parse(
                ingoIFrameParamsElement?.textContent ?? '{}'
            ) as IIngoIFrameParams;
        }

        const queryParamsKeysElement: HTMLScriptElement | null =
            document.getElementById('queryParamKeys') as HTMLScriptElement;
        if (Boolean(queryParamsKeysElement?.textContent)) {
            this.queryParamKeys = JSON.parse(
                queryParamsKeysElement?.textContent ?? '{}'
            ) as IQueryParamKeys;
        }

        // Determine funding destination from query param or default to 'bank_account'
        const fundingDestinationUrl: URL = new URL(location.href);
        const fundingDestinationQueryParam: string | null =
            fundingDestinationUrl.searchParams.get(
                this.queryParamKeys?.paymentType ?? ''
            );

        this.fundingDestination =
            this.ingoInstantPayments.FUNDING_DESTINATIONS.DEBIT;

        switch (fundingDestinationQueryParam) {
            case this.fundingDestinations.ach:
                this.fundingDestination =
                    this.ingoInstantPayments.FUNDING_DESTINATIONS.ACH;
                break;
            case this.fundingDestinations.directDebit:
                this.fundingDestination =
                    this.ingoInstantPayments.FUNDING_DESTINATIONS.DEBIT;
                break;
            case this.fundingDestinations.paperCheck:
                this.fundingDestination =
                    this.ingoInstantPayments.FUNDING_DESTINATIONS.BILLPAY;
                break;
        }
    }

    /**
     * Broadcasts test events for Playwright testing framework
     * Only active when __PLAYWRIGHT_TEST__ flag is present
     */
    private broadcastTestEvent(eventType: string, data: any): void {
        if ((window as any).__PLAYWRIGHT_TEST__) {
            const customEvent = new CustomEvent(`ingo:${eventType}`, {
                detail: {
                    data: data,
                    timestamp: new Date().toISOString(),
                    eventType: eventType
                }
            });
            document.dispatchEvent(customEvent);

            // Also store in global array for direct access
            if (!(window as any).__CAPTURED_INGO_EVENTS__) {
                (window as any).__CAPTURED_INGO_EVENTS__ = [];
            }
            (window as any).__CAPTURED_INGO_EVENTS__.push({
                type: eventType,
                data: data,
                timestamp: new Date().toISOString()
            });
        }
    }

    /**
     * Hides the loading indicator
     * Called on any completion event (success, error, or cancellation)
     */
    private hideLoadingIndicator(): void {
        const loadingIndicator = document.getElementById(
            IngoIFrame.LoadingIndicatorId
        );
        if (loadingIndicator) {
            // Use multiple methods to ensure the loading indicator is hidden
            // This prevents z-index issues where the loading indicator blocks dialogs
            loadingIndicator.style.display = 'none';
            loadingIndicator.style.visibility = 'hidden';
            loadingIndicator.style.opacity = '0';
            loadingIndicator.style.pointerEvents = 'none';
            // Also set z-index to negative to ensure it's behind everything
            loadingIndicator.style.zIndex = '-1';
        }
    }

    /**
     * Starts the loading timeout timer.
     * If the iframe doesn't fire a PAGE_LOAD event within the timeout duration,
     * an error dialog is shown.
     *
     * This timeout only fires once - it clears permanently when the iframe loads.
     */
    private startLoadingTimeout(): void {
        // Clear any existing timeout first (shouldn't happen, but be defensive)
        this.clearLoadingTimeout();

        console.warn(
            `[IngoIFrame] Loading timeout started: ${this.loadingTimeoutDuration}ms`
        );
        this.loadingTimeoutId = window.setTimeout(() => {
            this.handleLoadingTimeout();
        }, this.loadingTimeoutDuration);
    }

    /**
     * Clears the loading timeout timer.
     * Safe to call multiple times - will only clear if a timeout is active.
     */
    private clearLoadingTimeout(): void {
        if (this.loadingTimeoutId !== undefined) {
            window.clearTimeout(this.loadingTimeoutId);
            this.loadingTimeoutId = undefined;
        }
    }

    /**
     * Handles loading timeout - called when iframe fails to load within timeout duration.
     * Only shows error dialog if the iframe hasn't loaded yet.
     */
    private handleLoadingTimeout(): void {
        console.warn('[IngoIFrame] Loading timeout fired');
        // Double-check that we haven't loaded (shouldn't happen, but be defensive)
        if (this.hasLoaded) {
            console.warn('[IngoIFrame] Already loaded, skipping error dialog');
            return;
        }

        console.warn('[IngoIFrame] Hiding loading indicator');
        this.hideLoadingIndicator();
        console.warn('[IngoIFrame] Showing error dialog');
        this.showErrorDialog();
    }

    /**
     * Destroys the IngoIFrame instance and cleans up resources
     * This resets the module-level singleton so a new instance can be created
     */
    destroy(): void {
        // Clear any pending loading timeout
        this.clearLoadingTimeout();

        // Reset the module-level singleton
        // This allows initializeIngoIFrame() to create a new instance when called again
        ingoIFrame = undefined;

        // Clean up the global instance reference for testing
        if ((window as any).__INGO_IFRAME_INSTANCE__) {
            delete (window as any).__INGO_IFRAME_INSTANCE__;
        }
    }

    private showErrorDialog(): void {
        // Show the error dialog
        (window as any)[`${IngoIFrame.IFrameErrorDialogId}-openDialog`]?.show();
    }

    private showRegistrationErrorDialog(): void {
        // Show the payment method not registered dialog
        (window as any)[
            `${IngoIFrame.PaymentMethodNotRegisteredDialogId}-openDialog`
        ]?.show();
    }

    private fundingCanceledHandler(data: IIngoFundingCancelledEvent): void {
        this.broadcastTestEvent('funding_canceled', data);
        this.clearLoadingTimeout(); // Clear timeout - we got an event
        this.hideLoadingIndicator();

        // Redirect to the payment method selection page when funding is canceled
        let paymentMethodUrl: URL;
        try {
            paymentMethodUrl = new URL(window.location.href);
            // Replace only the last segment 'request-payment' in the pathname with 'payment-method'
            // This prevents issues if 'request-payment' appears elsewhere in the URL
            const pathSegments = paymentMethodUrl.pathname.split('/');
            if (pathSegments[pathSegments.length - 1] === 'request-payment') {
                pathSegments[pathSegments.length - 1] = 'payment-method';
                paymentMethodUrl.pathname = pathSegments.join('/');
            } else {
                // Fallback to original string replacement method
                paymentMethodUrl = new URL(
                    window.location.href.replace(
                        'request-payment',
                        'payment-method'
                    )
                );
            }
        } catch (error) {
            // Fallback to original string replacement method
            paymentMethodUrl = new URL(
                window.location.href.replace(
                    'request-payment',
                    'payment-method'
                )
            );
        }
        paymentMethodUrl.searchParams.delete(this.queryParamKeys.paymentType);

        window.location.href = paymentMethodUrl.href;
    }

    private maxVerificationAttemptsExceededHandler(
        data: IIngoMaxVerificationAttemptsExceededEvent
    ): void {
        this.broadcastTestEvent('max_verification_attempts_exceeded', data);
        this.clearLoadingTimeout(); // Clear timeout - we got an event
        this.hideLoadingIndicator();

        this.showRegistrationErrorDialog();
    }

    private pluginErrorHandler(data: IIngoPluginErrorEvent): void {
        this.broadcastTestEvent('plugin_error', data);
        this.clearLoadingTimeout(); // Clear timeout - we got an event
        this.hideLoadingIndicator();

        this.showErrorDialog();
    }

    private pageLoadHandler(data: IIngoPageLoadEvent): void {
        this.broadcastTestEvent('page_load', data);

        // Mark as loaded and clear the loading timeout permanently
        // The iframe has successfully loaded - no need to show timeout error
        this.hasLoaded = true;
        this.clearLoadingTimeout();

        this.hideLoadingIndicator();
    }

    private sessionErrorHandler(data: IIngoSessionErrorEvent): void {
        this.broadcastTestEvent('session_error', data);
        this.clearLoadingTimeout(); // Clear timeout - we got an event
        this.hideLoadingIndicator();

        // Show error dialog for any session error (including SessionTimeout)
        this.showErrorDialog();
    }

    private tokenSuccessHandler(data: IIngoTokenSuccessEvent): void {
        this.broadcastTestEvent('token_success', data);
        this.clearLoadingTimeout(); // Clear timeout - we got an event
        this.hideLoadingIndicator();

        const { successDialog } = this.controls;
        if (successDialog) {
            const linkButton: HTMLAnchorElement | null =
                successDialog.querySelector('a[href]') as HTMLAnchorElement;

            if (linkButton) {
                const successUrl: URL = new URL(linkButton.href);
                successUrl.searchParams.set(
                    this.queryParamKeys.paymentToken,
                    data.customer_account_token
                );
                successUrl.searchParams.delete(this.queryParamKeys.paymentType);

                // Update the link button URL with the customer account token from the event data
                linkButton.href = successUrl.href;

                // Update the hx-get attribute
                const updatedUrl: URL = new URL(linkButton.href);
                linkButton.setAttribute(
                    'hx-get',
                    updatedUrl.href.replace(updatedUrl.origin, '')
                );
                (window as any)['htmx'].process(linkButton);
            }
        }

        // Show the payment method registered dialog
        (window as any)[
            `${IngoIFrame.PaymentMethodRegisteredDialogId}-openDialog`
        ]?.show();
    }

    private unsupportedBrowserDetectedHandler(
        data: IIngoUnsupportedBrowserDetectedEvent
    ): void {
        this.broadcastTestEvent('unsupported_browser_detected', data);
        this.clearLoadingTimeout(); // Clear timeout - we got an event
        this.hideLoadingIndicator();

        this.showErrorDialog();
    }

    private terminalFailureAcknowledgedHandler(
        data: IIngoTerminalFailureAcknowledgedEvent
    ): void {
        this.broadcastTestEvent('terminal_failure_acknowledged', data);
        this.clearLoadingTimeout(); // Clear timeout - we got an event
        this.hideLoadingIndicator();

        this.showErrorDialog();
    }

    init() {
        // Start the loading timeout FIRST, before registering event listeners.
        // This avoids a race condition where an event fires and calls
        // clearLoadingTimeout() *before* startLoadingTimeout() has been invoked.
        // In that case, clearLoadingTimeout() would do nothing (no timeout exists
        // yet), and then startLoadingTimeout() would create a timeout that is
        // never cleared, causing the loading timeout to fire even though we
        // already received an event.
        this.startLoadingTimeout();

        // Testing hook: Skip mounting to force timeout for manual testing
        // Usage: localStorage.setItem('ingo-skip-mount', 'true')
        //        localStorage.setItem('ingo-loading-timeout', '3000')
        // To reset: localStorage.removeItem('ingo-skip-mount')
        // NOTE: We intentionally do NOT clear the timeout here - the purpose of this
        // testing hook is to force the timeout to fire so we can test the error dialog.
        // By skipping the mount, no PAGE_LOAD event will fire, causing the timeout to trigger.
        if (localStorage.getItem('ingo-skip-mount') === 'true') {
            console.warn(
                '[IngoIFrame] Skip mount enabled - skipping iframe mount for testing'
            );
            return;
        }

        this.ingoPlugin = this.ingoInstantPayments.create(
            this.iframeContainerElement,
            {
                cssName: 'ingo-payment-method-frame',
                autoHeight: true
            }
        );

        this.ingoPlugin.mount(
            this.ingoIFrameParams.authorizedMountingUrl,
            this.fundingDestination
        );

        this.ingoPlugin.addEventListener(
            this.ingoInstantPayments.EVENTS.FUNDING_CANCELED,
            this.fundingCanceledHandler.bind(this)
        );

        this.ingoPlugin.addEventListener(
            this.ingoInstantPayments.EVENTS.MAX_VERIFICATION_ATTEMPTS_EXCEEDED,
            this.maxVerificationAttemptsExceededHandler.bind(this)
        );

        this.ingoPlugin.addEventListener(
            this.ingoInstantPayments.EVENTS.PLUGIN_ERROR,
            this.pluginErrorHandler.bind(this)
        );

        this.ingoPlugin.addEventListener(
            this.ingoInstantPayments.EVENTS.PAGE_LOAD,
            this.pageLoadHandler.bind(this)
        );

        this.ingoPlugin.addEventListener(
            this.ingoInstantPayments.EVENTS.SESSION_ERROR,
            this.sessionErrorHandler.bind(this)
        );

        this.ingoPlugin.addEventListener(
            this.ingoInstantPayments.EVENTS.TOKEN_SUCCESS,
            this.tokenSuccessHandler.bind(this)
        );

        this.ingoPlugin.addEventListener(
            this.ingoInstantPayments.EVENTS.UNSUPPORTED_BROWSER_DETECTED,
            this.unsupportedBrowserDetectedHandler.bind(this)
        );

        this.ingoPlugin.addEventListener(
            this.ingoInstantPayments.EVENTS.TERMINAL_FAILURE_ACKNOWLEDGED,
            this.terminalFailureAcknowledgedHandler.bind(this)
        );
    }
}

let ingoIFrame: IngoIFrame | undefined = undefined;

(window as any)['initializeIngoIFrame'] = () => {
    // If an old instance exists, destroy it first
    // This handles htmx navigation where the DOM is swapped but the old instance still exists in memory
    if (ingoIFrame) {
        ingoIFrame.destroy();
    }

    // Clean up any leftover page transition spinners before initializing
    // HTMX adds spinners to pageContent during navigation but doesn't always remove them
    const oldSpinner = document.getElementById('spinnerBackdrop');
    if (oldSpinner) {
        // Verify this is safe to remove by checking if it's in expected locations:
        // 1. Body level (page-wide spinner)
        // 2. pageContent (HTMX main swap target - where HTMX adds spinners)
        const parent = oldSpinner.parentElement;
        const isBodyLevel = parent === document.body;
        const isInPageContent = parent?.id === 'pageContent';

        if (isBodyLevel || isInPageContent) {
            oldSpinner.remove();
        }
    }

    ingoIFrame = new IngoIFrame();

    // Expose instance globally for Playwright testing
    if ((window as any).__PLAYWRIGHT_TEST__) {
        (window as any).__INGO_IFRAME_INSTANCE__ = ingoIFrame;
    }

    ingoIFrame.init();
};
